/**
 * Design tokens for the HTEC OneLoopAi Blueprints theme.
 *
 * These are the single source of truth for the values that the MUI theme
 * (`htecTheme.ts`) consumes. MUI's palette only exposes three slots per role
 * (`main`/`light`/`dark`), which cannot hold the full five-tier semantic
 * status model, so the complete tiers live here instead.
 */

/**
 * Semantic status colours — HTEC OneLoopAi Status Color Guidelines (v1.0).
 *
 * Four semantic roles, each with five tiers (bg / border / icon / text /
 * strong). Usage intent:
 *  - Information (blue): active / selected / in-progress / available / AI.
 *  - Success (green):    confirmed success / completed only.
 *  - Warning (amber):    at-risk / attention / below-target.
 *  - Error (red):        critical / failed / blocked only.
 *
 * The HTEC brand green (`brand.primary`) is brand/primary only and MUST NOT
 * double as the Success semantic.
 */
export const statusColors = {
  info: {
    bg: '#F5F8FE',
    border: '#DFE6F6',
    icon: '#446BC2',
    text: '#1848B3',
    strong: '#133A8F',
  },
  success: {
    bg: '#F0FFEB',
    border: '#C0DEB5',
    icon: '#5B7F4D',
    text: '#46623B',
    strong: '#32462A',
  },
  warning: {
    bg: '#FFFAEB',
    border: '#F8E8B3',
    icon: '#BA9C3C',
    text: '#8A742C',
    strong: '#594B1D',
  },
  error: {
    bg: '#FFEBEB',
    border: '#ECACA9',
    icon: '#C6433E',
    text: '#772825',
    strong: '#501B19',
  },
} as const;

/** Brand palette. `primary` is the HTEC brand green (brand/navigation only). */
export const brand = {
  primary: '#2A5A4F',
  secondary: '#FF5630',
  highlight: '#4FD1C5',
} as const;

/**
 * Border-radius scale. Replaces the previously scattered literals
 * (0 / 3 / 8 / 12 / 15px) with a small named set. Values match the current
 * look (consistency, not redesign).
 */
export const radius = {
  none: 0, // alerts
  sm: 3, // buttons
  md: 8, // inputs, selects, autocomplete, sign-in button
  lg: 12, // chips / tags, sidebar icon container
  xl: 15, // paper / cards / panels, header, tabs, sidebar item
} as const;

/** Shared surface recipe (white card on subtle border with a soft shadow). */
export const surface = {
  background: '#FFFFFF',
  border: '1px solid #E2E8F0',
  shadow: '0px 3.5px 5.5px 0px rgba(0, 0, 0, 0.02)',
} as const;
